import { Box, Button, Checkbox, FormControl, FormControlLabel, InputLabel, MenuItem, Radio, RadioGroup, Select, TextField } from '@mui/material';
import { DataGrid, useGridApiRef } from '@mui/x-data-grid';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Customer } from '../../interfaces/Customer';
import { Provider } from '../../interfaces/Provider';
import { api } from '../../services';
import LogoutButton from '../LogoutButton';


const CustomersPage: React.FC = () => {
    const [customers, setCustomers] = useState<Customer[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [selectedCustomers, setSelectedCustomers] = useState<string[]>([]);
    const [provider, setProvider] = useState<Provider>(Provider.KDK);
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const gridApiRef = useGridApiRef();
    const [qualifiedState, setQualifiedState] = useState<Record<string, boolean>>({});
    const [initialQualifiedState, setInitialQualifiedState] = useState<Record<string, boolean>>({});
    const [filteringSelfEmployed, setFilteringSelfEmployed] = useState(false)
    const [filteringMicro, setFilteringMicro] = useState(false)
    const [filteringEmployees2Plus, setFilteringEmployees2Plus] = useState(false)
    const [filteringStage, setFilteringStage] = useState<String>('incomplete');
    const [backupCustomers, setBackupCustomers] = useState<Customer[]>([])
    const [searchQuery, setSearchQuery] = useState<string>('');
    useEffect(() => {
        if (!localStorage.getItem('token')) {
            navigate('/login');
        }
    }, [navigate]);

    useEffect(() => {
        fetchCustomers();
    }, []);

    const handleQualifiedChange = (nip: string, isChecked: boolean) => {
        setQualifiedState((prevState) => ({
            ...prevState,
            [nip]: isChecked,
        }));
    };

    const handleQualifiedSubmit = async () => {
        setIsLoading(true);
        const nipsToQualify: string[] = [];
        const nipsToUnQualify: string[] = [];

        customers.forEach((c) => {
            const initial = initialQualifiedState[c.nip] ?? false;
            const current = qualifiedState[c.nip] ?? false;

            if (initial !== current) {
                if (current) nipsToQualify.push(c.nip);
                else nipsToUnQualify.push(c.nip);
            }
        });

        if (nipsToQualify.length === 0 && nipsToUnQualify.length === 0) {
            enqueueSnackbar('Brak zmian do zapisania.', { variant: 'info' });
            return;
        }

        try {
            await api.updateQualifiedCustomers({ nipsToQualify, nipsToUnQualify });
            enqueueSnackbar('Pomyślnie zmieniono stan zakwalifikowania!', { variant: 'success' });

            setInitialQualifiedState({ ...qualifiedState });
        } catch (error) {
            console.error('Błąd serwera', error);
            enqueueSnackbar('Błąd serwera', { variant: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const fetchCustomers = async () => {
        setIsLoading(true);
        try {
            const fetchedCustomers = await api.fetchCustomers();
            const sortedCustomers = fetchedCustomers.sort((a, b) => b.createdAt - a.createdAt);
            setBackupCustomers(sortedCustomers);

            const qualifiedMap: Record<string, boolean> = {};
            sortedCustomers.forEach((customer) => {
                qualifiedMap[customer.nip] = customer.qualified;
            });

            setQualifiedState(qualifiedMap);
            setInitialQualifiedState(qualifiedMap);
        } catch (e) {
            console.error(e);
            enqueueSnackbar('Error fetching customers', { variant: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchCustomers()
    }, []);

    const handleDownload = async () => {
        if (selectedCustomers.length === 0) {
            enqueueSnackbar('Wybierz co najmniej jednego klienta aby wygenerować załączniki', { variant: 'warning' });
            return;
        }

        try {
            setIsLoading(true);
            const selectedCustomerObjects = backupCustomers.filter((c) => selectedCustomers.includes(String(c.nip)));
            enqueueSnackbar('Rozpoczęto proces generowania załączników', { variant: 'warning' });
            await api.downloadDocuments(selectedCustomerObjects, provider);
            enqueueSnackbar('Documents downloaded successfully', { variant: 'success' });
        } catch (error) {
            console.error("Error downloading documents:", error);
            enqueueSnackbar('Error downloading documents', { variant: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleQualifiedSendAndRefresh = async () => {
        await handleQualifiedSubmit();
        await fetchCustomers();
    };


    const filterCustomers = (customers: Customer[]) => {
        if (customers.length == 0) {
            return;
        }
        let filtered = [...customers];
        if (filteringStage === 'incomplete') {
            filtered = filtered.filter(c => !c.dataComplete && !c.qualified); // tylko niekompletni i niezakwalifikowani
        } else if (filteringStage === 'complete') {
            filtered = filtered.filter(c => c.dataComplete && !c.qualified); // tylko kompletni i niezakwalifikowani
        } else if (filteringStage === 'qualified') {
            filtered = filtered.filter(c => c.qualified); // tylko zakwalifikowani
        }

        if (filteringSelfEmployed) {
            filtered = filtered.filter(c => c.financials0.companySize === 'jdg');
        }

        if (filteringMicro) {
            filtered = filtered.filter(c => c.financials0.companySize === 'mikro');
        }

        if (filteringEmployees2Plus) {
            filtered = filtered.filter(c => c.financials0.employeesCount >= 2);
        }

        setCustomers(filtered);
    }

    useEffect(() => {
        filterCustomers(backupCustomers);
    }, [backupCustomers, , filteringSelfEmployed, filteringMicro, filteringEmployees2Plus, filteringStage]);

    return (
        <Box sx={{ padding: 2, maxWidth: 1200, margin: 'auto' }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 4, marginBottom: 2 }}>
                <FormControl sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2 }}>
                    <RadioGroup
                        row
                        value={filteringStage}
                        onChange={(event) => setFilteringStage(event.target.value)}
                        sx={{ alignItems: 'center' }}
                    >
                        <FormControlLabel
                            value="incomplete"
                            control={<Radio />}
                            label="Niekompletni"
                        />
                        <FormControlLabel
                            value="complete"
                            control={<Radio />}
                            label="Kompletni"
                        />
                        <FormControlLabel
                            value="qualified"
                            control={<Radio />}
                            label="Zakwalifikowani"
                        />
                    </RadioGroup>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={filteringSelfEmployed}
                                onChange={(event) => {
                                    setFilteringSelfEmployed(event.target.checked);
                                    if (event.target.checked) {
                                        setFilteringMicro(false);
                                        setFilteringEmployees2Plus(false);
                                    }
                                }}
                            />
                        }
                        label="Tylko JDG"
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={filteringMicro}
                                onChange={(event) => {
                                    setFilteringMicro(event.target.checked);
                                    if (event.target.checked) {
                                        setFilteringSelfEmployed(false);
                                        setFilteringEmployees2Plus(false);
                                    }
                                }}
                            />
                        }
                        label="Tylko mikroprzedsiębiorcy"
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={filteringEmployees2Plus}
                                onChange={(event) => {
                                    setFilteringEmployees2Plus(event.target.checked);
                                    if (event.target.checked) {
                                        setFilteringSelfEmployed(false);
                                        setFilteringMicro(false);
                                    }
                                }}
                            />
                        }
                        label="2 Pracowników i wiecej"
                    />
                </FormControl>
                <Button variant="contained" color="primary" onClick={() => navigate('/add-customer')}>
                    Dodaj klienta
                </Button>
                <Button variant="contained" color="secondary" disabled={isLoading} onClick={fetchCustomers}>
                    Odśwież
                </Button>
                <FormControl sx={{ minWidth: 150, marginBottom: 1 }} variant="outlined">
                    <InputLabel id="provider-select-label">Dostawca</InputLabel>
                    <Select
                        labelId="provider-select-label"
                        value={provider}
                        onChange={(e) => setProvider(e.target.value as Provider)}
                        label="Provider"
                    >
                        {Object.values(Provider).map((prov) => (
                            <MenuItem key={prov} value={prov}>{prov}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Button variant="contained" color="success" onClick={handleDownload} disabled={isLoading}>
                    Wygeneruj załączniki
                </Button>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        // Check if the search query is all digits or has no digits
                        const hasDigits = /\d/.test(searchQuery);
                        const isAllDigits = /^\d+$/.test(searchQuery);
                        if (isAllDigits) {
                            // searchQuery is all digits
                            const filteredCustomers = backupCustomers.filter(c => c.nip.includes(searchQuery));
                            setCustomers(filteredCustomers);
                            filterCustomers(filteredCustomers);
                        } else if (!hasDigits) {
                            // searchQuery has no digits
                            const filteredCustomers = backupCustomers.filter(c => c.surname.toLowerCase().includes(searchQuery.toLowerCase()));
                            setCustomers(filteredCustomers);
                            filterCustomers(filteredCustomers);
                        } else {
                            enqueueSnackbar('Wprowadzono jednocześnie cyfry i litery!', { variant: 'error' });
                        }
                    }}
                    style={{ width: '100%' }}
                >
                    <TextField
                        label="Szukaj klienta"
                        variant="outlined"
                        size="small"
                        sx={{ width: 300, mr: 2 }}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    <Button type="submit" variant="contained" color="primary">
                        Szukaj
                    </Button>
                </form>
            </Box>
            <Box
                sx={{
                    minHeight: 600,
                    maxHeight: 1200,
                    width: '100%',
                    '& .disabled-row': {
                        opacity: 0.5,
                    },
                    '& .MuiDataGrid-row.disabled-row .MuiDataGrid-cell:not(.qualified-cell)': {
                        pointerEvents: 'none',
                        transition: 'background-color 0.3s ease',
                    },
                    '& .filled-row': {
                        backgroundColor: '#d4edda',
                        transition: 'background-color 0.3s ease',
                    },
                    '& .filled-row:hover': {
                        backgroundColor: '#97c9a3 !important',
                        transition: 'background-color 0.3s ease',
                    },
                    '& .MuiDataGrid-row.Mui-selected': {
                        backgroundColor: '#97c9a3 !important',
                        transition: 'background-color 0.3s ease',
                    },
                    '& .MuiDataGrid-row.Mui-selected:hover': {
                        backgroundColor: '#97c9a3 !important',
                        transition: 'background-color 0.3s ease',
                    },

                    '& .MuiDataGrid-row:hover:not(.filled-row):not(.Mui-selected)': {
                        backgroundColor: '#f1f1f1',
                        transition: 'background-color 0.3s ease',
                    },
                    '& .MuiDataGrid-row.Mui-selected:not(.filled-row)': {
                        backgroundColor: '#f1f1f1 !important',
                        transition: 'background-color 0.3s ease',
                    },
                }}
            >
                <DataGrid
                    localeText={{
                        paginationRowsPerPage: 'Wierszy na stronę:'
                    }}
                    apiRef={gridApiRef}
                    rows={customers}
                    getRowId={(row) => String(row.nip)}
                    columns={[
                        { field: 'nip', headerName: 'NIP', flex: 1 },
                        { field: 'surname', headerName: 'Nazwisko', flex: 1, sortable: true, filterable: false },
                        { field: 'name', headerName: 'Imię', flex: 1, sortable: false, filterable: false },
                        { field: 'companyName', headerName: 'Nazwa Firmy', flex: 1, sortable: false, filterable: false },
                        { field: 'financials0.companySize', headerName: 'Rodzaj działalności', flex: 1, sortable: false, filterable: false, renderCell: (params) => (<>{params.row.financials0.companySize}</>) },
                        {
                            field: 'actions',
                            headerName: 'Edycja',
                            flex: 1,
                            sortable: false,
                            filterable: false,
                            cellClassName: 'qualified-cell',
                            renderCell: (params) => (
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => navigate(`/add-customer?nip=${params.row.nip}`)}
                                >
                                    Edytuj
                                </Button>
                            ),
                        },
                        {
                            field: 'Qualified',
                            headerName: 'Qualified',
                            flex: 0.5,
                            sortable: false,
                            filterable: false,
                            minWidth: 200,
                            cellClassName: 'qualified-cell',
                            renderHeader: () => (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <span>Zakwalifikowany</span>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        color="secondary"
                                        onClick={handleQualifiedSendAndRefresh}
                                        disabled={isLoading}
                                    >
                                        Zmień
                                    </Button>
                                </Box>
                            ),
                            renderCell: (params) => (
                                <Box display="flex" justifyContent="center" alignItems="center" width="100%">
                                    <Checkbox
                                        checked={qualifiedState[params.row.nip] || false}
                                        onChange={(e) => handleQualifiedChange(params.row.nip, e.target.checked)}
                                    />
                                </Box>
                            )
                        },
                        { field: 'createdAt', headerName: 'Data Dodania', flex: 0.5, minWidth: 120, sortable: true, filterable: true, renderCell: (params) => (<>{new Date(params.row.createdAt).toLocaleDateString("pl-PL")}</>) },
                        { field: 'createdBy', headerName: 'Kto Dodał', flex: 0.5, minWidth: 100, sortable: false, filterable: true }
                    ]}
                    checkboxSelection
                    onRowSelectionModelChange={(selectionModel) => {
                        if (selectionModel && typeof selectionModel === 'object' && 'ids' in selectionModel) {
                            const selectedIds = Array.from(selectionModel.ids).map(String);
                            setSelectedCustomers(selectedIds);
                        } else {
                            setSelectedCustomers([]);
                        }
                    }}
                    loading={isLoading}
                    getRowClassName={(params) => {
                        const nip = params.row.nip;
                        const isQualified = qualifiedState[nip];
                        const isFilled = params.row.dataComplete;

                        if (isQualified) return 'disabled-row';
                        if (isFilled) return 'filled-row';
                        return '';
                    }}
                    isRowSelectable={(params) =>
                        !qualifiedState[params.row.nip]
                    }
                />
            </Box>
            <LogoutButton />
        </Box >
    );
};

export default CustomersPage;